import { useDetectedBy } from "~/components/vulnerabilities/DetectedBy/hooks/useDetectedBy";
import { useFindingOnAsset } from "./useFindingOnAsset";
import { mapDetectedByVendor } from "~/components/vulnerabilities/DetectedBy/lib";
import {
  createAssetOverviewStringValue,
  createAssetOverviewTimeValue,
  extractAssetKey,
  extractAssetLink,
  extractAssetValue,
} from "~/lib/assetUtils";
import { isAdvisoryOrCveFinding } from "~/pages/findings/FindingOnAsset/utils";
import { useCanUpdate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Update";

export function useAssetInsights() {
  const { finding, asset, activeScope, spaceScope } = useFindingOnAsset();
  const { overviewGroups } = asset || {};
  const { firstFound } = useDetectedBy({
    detectedBy: isAdvisoryOrCveFinding(finding)
      ? finding?.detectionSources || []
      : [],
  });

  const hasIntegrationUpdatePermission = useCanUpdate();

  const assetOverviewGroupItems = asset?.overviewGroups.flatMap(
    (group) => group.items,
  );

  const assetOverviewGroupItemsLinks = assetOverviewGroupItems
    ?.map((item) => {
      const link = extractAssetLink(
        item,
        spaceScope?.params.toString() || "",
        hasIntegrationUpdatePermission,
      );
      const key = extractAssetKey(item);
      const name = extractAssetValue(item);

      return link ? { link, key, name } : null;
    })
    .filter(Boolean);

  const linkToAsset = `/space/inventory/${asset?.id}/overview?${activeScope?.params}`;

  const platformGroup = overviewGroups?.find(
    (g) => g.title === "Platform overview",
  );

  const evidenceGroup = overviewGroups?.find(
    (g) => g.title === "Scan overview",
  ) || { items: [] };

  //copy the evidence group to an object that is modifiable
  const evidenceGroupCopy = JSON.parse(JSON.stringify(evidenceGroup));

  const evidenceToAdd = [
    {
      key: "first-detected-at",
      title: "First detected at",
      value: firstFound?.firstDetectedAt,
      type: "time",
    },
    {
      key: "last-updated-at",
      title: "Last updated at",
      value: firstFound?.lastUpdatedAt,
      type: "time",
    },
    firstFound?.vendor
      ? {
          key: "vendor",
          title: "Vendor",
          value: firstFound?.vendor,
          type: "string",
        }
      : null,
  ].flatMap((x) => x ?? []);

  evidenceToAdd.forEach((evidence) => {
    evidenceGroupCopy.items.push(
      evidence.type === "time"
        ? createAssetOverviewTimeValue(
            evidence.key,
            evidence.title,
            evidence.value,
          )
        : createAssetOverviewStringValue(
            evidence.key,
            evidence.title,
            mapDetectedByVendor(evidence.value),
          ),
    );
  });

  return {
    evidenceGroup: evidenceGroupCopy,
    platformGroup,
    linkToAsset,
    finding,
    assetOverviewGroupItemsLinks,
  };
}
